import { Button } from "@components/ui/Button"
import { useAuth } from "@features/accounts/useAuth"
import { formatDollars } from "@utils/format-dollars"
import { cn } from "@utils/tw"
import { defineBlock, EditableText, InnerBlocks, useInlineEditableValue } from "eddev/blocks"
import { AnimatePresence, motion, MotionProps } from "motion/react"
import { useState } from "react"
import { MembershipGridContext } from "../../contexts/MembershipGridContext"

export const meta: BlockMeta = {
  title: "Membership Grid",
  description: "Displays the available membership levels in an extended grid",
  category: "text",
  icon: "admin-users",
  tags: ["subpage"],
  flags: {
    fullWidth: true,
  },
}

const Cell = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div className={cn("shrink-0 p-4 border-r border-grey2 last:border-none flex justify-start items-end", className)}>
    {children}
  </div>
)

export default defineBlock("memberships/membership-grid", (props) => {
  const auth = useAuth()
  const levels = props.miscSettings?.memberships?.membershipLevels ?? []
  const [mode, setMode] = useState<"premium" | "standard">("standard")

  return (
    <div className="w-full overflow-auto my-11 px-4 md:px-0">
      <div className="flex flex-col justify-center items-center min-w-fit">
        <div className="flex justify-center shrink-0 shadow-header w-fit">
          {/* HEADERS */}
          <Cell className="w-membershipGridCol1W">
            <div className="flex items-center flex-nowrap">
              <Button
                onClick={() => setMode("standard")}
                variant={mode === "standard" ? "default" : "secondary"}
                size="sm"
                className={cn(mode === "standard" ? "opacity-100" : "opacity-50")}
              >
                Standard
              </Button>
              <Button
                onClick={() => setMode("premium")}
                variant={mode === "premium" ? "default" : "secondary"}
                size="sm"
                className={cn(mode === "premium" ? "opacity-100" : "opacity-50", "!bg-yellow !text-black")}
              >
                Premium
              </Button>
            </div>
          </Cell>
          {levels.map((level, i) => {
            let price = formatDollars(level?.priceStandard!)
            if (mode === "premium" && level?.hasPremium) {
              price = formatDollars(level?.pricePremium!)
            }

            return (
              <Cell key={i} className="w-membershipGridColW">
                <div className="flex flex-col w-full">
                  <p className="type-label-xl-2 uppercase">{level?.label}</p>
                  <p className="type-body-m opacity-50">{price}</p>
                  <Button
                    className="mt-5"
                    size="sm"
                    onClick={() => {
                      auth.openModal("member-signup-form", {
                        eventId: level?.squadupId!,
                      })
                    }}
                  >
                    Join
                  </Button>
                </div>
              </Cell>
            )
          })}
        </div>

        {/* BODY */}
        <div className="flex flex-col justify-center shrink-0 mt-7 w-fit">
          <MembershipGridContext.Provider value={{ mode, totalLevels: levels.length }}>
            <InnerBlocks
              allowedBlocks={["memberships/membership-grid-group"]}
              orientation="vertical"
              defaultBlocks={[
                ["memberships/membership-grid-group", {}],
                ["memberships/membership-grid-group", {}],
                ["memberships/membership-grid-group", {}],
              ]}
            />
          </MembershipGridContext.Provider>
        </div>
      </div>
    </div>
  )
})
